import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useMutation } from "@apollo/client/react";
import { useLocation } from "react-router";
import { gql } from "@apollo/client";
import { MessageCircleMoreIcon } from "lucide-react";
import { useCurrentSpace, useProfile } from "~/hooks/context-hook";
import { resolveCustomerSupport } from "~/hooks/customer-support-policy";
import { useConfig } from "~/hooks/webconfig";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface HubSpotVisitorTokenData {
  hubspotVisitorToken: {
    token: string;
  };
}

type HubSpotUnreadPayload =
  | number
  | {
      unreadCount?: number;
      unreadConversationCount?: number;
    };

declare global {
  interface Window {
    HubSpotConversations?: {
      widget: {
        load: (options?: { widgetOpen?: boolean }) => void;
        open: () => void;
        close: () => void;
        remove: () => void;
        refresh: (openToNewThread?: boolean) => void;
        status: () => { loaded: boolean; pending: boolean };
      };
      on: (
        event: string,
        callback: (payload?: HubSpotUnreadPayload) => void,
      ) => void;
      off: (
        event: string,
        callback: (payload?: HubSpotUnreadPayload) => void,
      ) => void;
    };
    hsConversationsOnReady?: Array<() => void>;
    hsConversationsSettings?: {
      loadImmediately?: boolean;
      identificationEmail?: string;
      identificationToken?: string;
    };
  }
}

/* eslint-disable lingui/no-unlocalized-strings -- TS type-import module path */
const HUBSPOT_VISITOR_TOKEN_MUTATION = gql`
  mutation HubspotVisitorToken {
    hubspotVisitorToken {
      token
    }
  }
` as import("@graphql-typed-document-node/core").TypedDocumentNode<HubSpotVisitorTokenData>;
/* eslint-enable lingui/no-unlocalized-strings */

const HubSpotContext = createContext<{ open: () => void }>({
  open: () => {},
});

export const useHubSpot = () => useContext(HubSpotContext);

export default function HubSpotInitializer({
  children,
}: React.PropsWithChildren<{}>) {
  const config = useConfig();
  const user = useProfile();
  // Stage 7: support is a property of the CURRENT Mindspace, not of the person.
  // This replaces the old person-wide `currentUser.selfServePolicy` gate — a
  // learner who is self-serve in one space and Live in another must follow the
  // space they are actually looking at. `resolveCustomerSupport` fails toward
  // ENABLED, so an absent field never silently unloads a Live support widget.
  const currentSpace = useCurrentSpace();
  const supportChatDisabled = !resolveCustomerSupport(currentSpace);
  const pendingOpenRef = useRef(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [getVisitorToken] = useMutation(HUBSPOT_VISITOR_TOKEN_MUTATION);

  const openWidget = useCallback(() => {
    if (supportChatDisabled) return;
    if (typeof window === "undefined" || !window.HubSpotConversations) return;

    setHasUnreadMessages(false);

    const widget = window.HubSpotConversations.widget;
    if (widget.status().loaded) {
      document.body.classList.remove("mindstone-hubspot-launcher-hidden");
      widget.open();
      return;
    }

    widget.load({ widgetOpen: true });
    window.setTimeout(() => {
      document.body.classList.remove("mindstone-hubspot-launcher-hidden");
      window.HubSpotConversations?.widget.open();
    }, 250);
  }, [supportChatDisabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (supportChatDisabled) {
      pendingOpenRef.current = false;
      setHasUnreadMessages(false);
      window.HubSpotConversations?.widget.remove();
      window.hsConversationsOnReady = [];
      window.hsConversationsSettings = {
        loadImmediately: false,
      };
      document.getElementById("hs-script-loader")?.remove();
      document.body.classList.remove("mindstone-hubspot-launcher-hidden");
      return;
    }
    if (!config.hubspotPortalId) return;
    if (document.getElementById("hs-script-loader")) return;

    document.body.classList.add("mindstone-hubspot-launcher-hidden");
    window.hsConversationsSettings = {
      loadImmediately: false,
    };

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "hs-script-loader";
    script.async = true;
    script.defer = true;
    // eslint-disable-next-line lingui/no-unlocalized-strings -- HubSpot script URL
    script.src = `//js-eu1.hs-scripts.com/${config.hubspotPortalId}.js`;
    document.body.appendChild(script);
  }, [config.hubspotPortalId, supportChatDisabled]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !config.hubspotPortalId ||
      supportChatDisabled
    ) {
      return;
    }

    if (!user) {
      window.HubSpotConversations?.widget.remove();
      window.hsConversationsSettings = {
        loadImmediately: false,
      };
      return;
    }

    const identify = async () => {
      try {
        const { data } = await getVisitorToken();
        const token = data?.hubspotVisitorToken?.token;

        if (token) {
          window.hsConversationsSettings = {
            ...window.hsConversationsSettings,
            identificationEmail: user.email || undefined,
            identificationToken: token,
          };
        }
      } catch {
        // Fall through to load widget without identification
      }

      if (window.HubSpotConversations) {
        if (pendingOpenRef.current) {
          pendingOpenRef.current = false;
          openWidget();
        } else {
          window.HubSpotConversations.widget.load();
        }
      } else {
        window.hsConversationsOnReady = [
          ...(window.hsConversationsOnReady || []),
          () => {
            if (pendingOpenRef.current) {
              pendingOpenRef.current = false;
              openWidget();
            } else {
              window.HubSpotConversations?.widget.load();
            }
          },
        ];
      }
    };

    identify();

    return () => {
      window.HubSpotConversations?.widget.remove();
    };
  }, [
    user,
    config.hubspotPortalId,
    supportChatDisabled,
    getVisitorToken,
    openWidget,
  ]);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !config.hubspotPortalId ||
      supportChatDisabled
    ) {
      return;
    }

    const hideLauncher = () => {
      document.body.classList.add("mindstone-hubspot-launcher-hidden");
    };

    const showWidget = () => {
      document.body.classList.remove("mindstone-hubspot-launcher-hidden");
      setHasUnreadMessages(false);
    };

    const updateUnreadState = (payload?: HubSpotUnreadPayload) => {
      const count =
        typeof payload === "number"
          ? payload
          : (payload?.unreadCount ?? payload?.unreadConversationCount ?? 0);

      setHasUnreadMessages(count > 0);
    };

    const registerHandlers = () => {
      window.HubSpotConversations?.on("widgetClosed", hideLauncher);
      window.HubSpotConversations?.on("widgetOpened", showWidget);
      window.HubSpotConversations?.on(
        "unreadConversationCountChanged",
        updateUnreadState,
      );
    };

    if (window.HubSpotConversations) {
      registerHandlers();
    } else {
      window.hsConversationsOnReady = [
        ...(window.hsConversationsOnReady || []),
        registerHandlers,
      ];
    }

    return () => {
      window.HubSpotConversations?.off("widgetClosed", hideLauncher);
      window.HubSpotConversations?.off("widgetOpened", showWidget);
      window.HubSpotConversations?.off(
        "unreadConversationCountChanged",
        updateUnreadState,
      );
      document.body.classList.remove("mindstone-hubspot-launcher-hidden");
    };
  }, [config.hubspotPortalId, supportChatDisabled]);

  const open = useCallback(() => {
    if (supportChatDisabled) return;
    if (typeof window !== "undefined" && window.HubSpotConversations) {
      openWidget();
      return;
    }

    pendingOpenRef.current = true;
    if (typeof window === "undefined") return;

    window.hsConversationsOnReady = [
      ...(window.hsConversationsOnReady || []),
      () => {
        if (!pendingOpenRef.current) return;
        pendingOpenRef.current = false;
        openWidget();
      },
    ];
  }, [openWidget, supportChatDisabled]);

  const { t } = useLingui();
  // The Rebel launch bridge (/launch/*) is a single-purpose page: no support
  // rail, no coach launcher slot (open-in-rebel-learning plan, UI Brief C).
  const onLaunchBridge = useLocation().pathname.startsWith("/launch/");
  return (
    <HubSpotContext.Provider value={{ open }}>
      {children}
      {/*
        Right-edge rail. Owns the vertical centring and the gap for every tab
        docked to the viewport edge, so the tabs cannot drift apart or overlap
        as either one changes size (translated labels, unread badge).

        Contents, top to bottom:
          1. the AI coach launcher, portalled in by the shared live-avatar-coach
             widget via `data-coach-launcher-slot` (see
             components/live-avatar-coach-mount.tsx). `display: contents` so the
             portalled <button> is itself the flex item.
          2. this app's "Chat to us" support tab.

        Coach above chat is deliberate (UX call, 2026-08-14): the coach is the
        first-line affordance and human support sits under it as the fallback.
        The rail renders even without a HubSpot portal id, so the coach still
        has a home when the support tab is absent.

        It is centred on the screen above a bottom action bar (6rem, the
        coach panel's BOTTOM_BAR_PANEL_OFFSET), not on the whole screen: on a
        short screen (1280x720 at 150% zoom and up) a whole-screen centre put
        the "Chat to us" tab over the edge of the personaliser's Start and the
        program-day Next (FOX-6892).
      */}
      {onLaunchBridge ? null : (
      <div className="fixed right-0 top-[calc(50%-3rem)] z-50 flex -translate-y-1/2 flex-col items-end gap-3">
        <div data-coach-launcher-slot className="contents" />
        {!supportChatDisabled && config.hubspotPortalId ? (
          <button
            type="button"
            onClick={open}
            className="group flex min-h-32 w-8 cursor-pointer items-center justify-center rounded-l-xl border border-r-0 border-quaternary-hover/80 bg-quaternary-hover px-1 py-3 text-quaternary-foreground shadow-md shadow-primary/10 transition-all duration-200 ease-out hover:w-9 hover:-translate-x-0.5 hover:bg-quaternary hover:shadow-lg focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background active:translate-x-0"
            aria-label={
              hasUnreadMessages
                ? t(msg`Chat to us, unread messages`)
                : t(msg`Chat to us`)
            }
          >
            {hasUnreadMessages ? (
              <span className="absolute left-1/2 top-1 size-2.5 -translate-x-1/2 rounded-full bg-destructive ring-1 ring-background" />
            ) : null}
            <span className="flex items-center gap-1.5 [writing-mode:vertical-rl] rotate-180">
              <MessageCircleMoreIcon
                className="size-4 shrink-0 transition-transform duration-200 group-hover:-translate-y-0.5"
                aria-hidden="true"
              />
              <span className="text-sm font-semibold leading-none tracking-normal">
                <Trans>Chat to us</Trans>
              </span>
            </span>
          </button>
        ) : null}
      </div>
      )}
    </HubSpotContext.Provider>
  );
}
